/**
 * DialogReceberPedido.jsx
 * Modal para confirmar entrada de estoque com seleção de local de armazenamento
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Package, Warehouse, AlertCircle, Check, Plus, Minus, Truck } from 'lucide-react';
import axios from 'axios';
import api from '../../lib/axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const DialogReceberPedido = ({ 
  open, 
  onClose, 
  pedido, 
  onSuccess,
  getAuthHeader 
}) => {
  const [locaisEstoque, setLocaisEstoque] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // 'estoque' = dá entrada no estoque | 'entrega_direta' = fornecedor entregou direto ao cliente (não movimenta estoque)
  const [tipoEntrega, setTipoEntrega] = useState('estoque');
  
  // Estado para cada item com quantidade e distribuição por local
  const [itensRecebimento, setItensRecebimento] = useState([]);
  // Comentário/observação do recebimento
  const [observacao, setObservacao] = useState('');
  // Local único para aplicar a todos os itens de uma vez
  const [localGlobal, setLocalGlobal] = useState('');

  // Carregar locais de estoque ao abrir o modal
  useEffect(() => {
    if (open && pedido) {
      setTipoEntrega('estoque');
      setObservacao('');
      setLocalGlobal('');
      fetchLocaisEstoque();
      initializeItens();
    }
  }, [open, pedido]);

  const fetchLocaisEstoque = async () => {
    setLoading(true);
    try {
      const response = await api.get('/locais-estoque');
      setLocaisEstoque(response.data || []);
    } catch (error) {
      console.error('Erro ao buscar locais de estoque:', error);
      toast.error('Erro ao carregar locais de estoque');
    } finally {
      setLoading(false);
    }
  };

  const initializeItens = () => {
    if (!pedido?.itens) return;
    
    const itens = pedido.itens.map((item, index) => {
      const quantidadePendente = (item.quantidade || 0) - (item.quantidade_recebida || 0);
      return {
        index,
        item_id: item.id,
        produto_id: item.produto_id,
        descricao: item.descricao,
        quantidade_pedida: item.quantidade || 0,
        quantidade_recebida_anterior: item.quantidade_recebida || 0,
        quantidade_pendente: quantidadePendente,
        // Distribuição por local - inicialmente um único local
        distribuicao: [{
          local_id: '',
          local_nome: '',
          quantidade: quantidadePendente
        }]
      };
    });
    
    setItensRecebimento(itens);
  };

  // Adicionar nova linha de local para um item
  const addLocalDistribuicao = (itemIndex) => {
    setItensRecebimento(prev => prev.map((item, idx) => {
      if (idx !== itemIndex) return item;
      return {
        ...item,
        distribuicao: [...item.distribuicao, { local_id: '', local_nome: '', quantidade: 0 }]
      };
    }));
  };

  // Remover linha de local de um item
  const removeLocalDistribuicao = (itemIndex, localIndex) => {
    setItensRecebimento(prev => prev.map((item, idx) => {
      if (idx !== itemIndex) return item;
      if (item.distribuicao.length <= 1) return item; // Manter pelo menos 1
      return {
        ...item,
        distribuicao: item.distribuicao.filter((_, i) => i !== localIndex)
      };
    }));
  };

  // Atualizar local de um item
  const updateLocalDistribuicao = (itemIndex, localIndex, field, value) => {
    setItensRecebimento(prev => prev.map((item, idx) => {
      if (idx !== itemIndex) return item;
      const newDistribuicao = item.distribuicao.map((dist, i) => {
        if (i !== localIndex) return dist;
        if (field === 'local_id') {
          const local = locaisEstoque.find(l => l.id === value);
          return { ...dist, local_id: value, local_nome: local?.nome || '' };
        }
        return { ...dist, [field]: field === 'quantidade' ? parseInt(value) || 0 : value };
      });
      return { ...item, distribuicao: newDistribuicao };
    }));
  };

  // Aplicar um único local a TODOS os itens (cada item recebe a qtd pendente total nesse local)
  const aplicarLocalTodos = (localId) => {
    setLocalGlobal(localId);
    if (!localId) return;
    const local = locaisEstoque.find(l => l.id === localId);
    setItensRecebimento(prev => prev.map(item => {
      if (item.quantidade_pendente <= 0) return item;
      return {
        ...item,
        distribuicao: [{
          local_id: localId,
          local_nome: local?.nome || '',
          quantidade: item.quantidade_pendente,
        }],
      };
    }));
  };

  // Calcular total distribuído por item
  const getTotalDistribuido = (item) => {
    return item.distribuicao.reduce((sum, dist) => sum + (dist.quantidade || 0), 0);
  };

  // Validar se a distribuição está correta
  const validarDistribuicao = () => {
    for (const item of itensRecebimento) {
      if (item.quantidade_pendente === 0) continue;
      
      const totalDistribuido = getTotalDistribuido(item);
      if (totalDistribuido > item.quantidade_pendente) {
        toast.error(`Item "${item.descricao}": quantidade (${totalDistribuido}) maior que pendente (${item.quantidade_pendente})`);
        return false;
      }
      
      // Local de armazenamento só é obrigatório quando dá entrada no estoque
      if (tipoEntrega === 'estoque') {
        for (const dist of item.distribuicao) {
          if (dist.quantidade > 0 && !dist.local_id) {
            toast.error(`Item "${item.descricao}": selecione o local de armazenamento`);
            return false;
          }
        }
      }
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validarDistribuicao()) return;
    
    setSubmitting(true);
    try {
      const entregaDireta = tipoEntrega === 'entrega_direta';
      // Preparar payload
      const payload = {
        tipo_entrega: tipoEntrega,
        observacao: observacao.trim() || null,
        itens: itensRecebimento.map(item => ({
          item_id: item.item_id,
          produto_id: item.produto_id,
          quantidade_recebida: getTotalDistribuido(item),
          index: item.index,
          // Entrega direta não movimenta estoque, então não envia distribuição por local
          distribuicao_locais: entregaDireta ? [] : item.distribuicao
            .filter(d => d.quantidade > 0 && d.local_id)
            .map(d => ({
              local_id: d.local_id,
              local_nome: d.local_nome,
              quantidade: d.quantidade
            }))
        })).filter(item => item.quantidade_recebida > 0)
      };
      
      const response = await api.post(`/pedidos-compra/${pedido.id}/receber`, payload);
      
      toast.success('Recebimento registrado com sucesso!');
      
      // Feedback
      const data = response.data;
      if (data.entrega_direta) {
        toast.info('Entrega direta ao cliente registrada. O estoque NÃO foi movimentado.');
      } else if (data.total_entradas_estoque > 0) {
        toast.success(`${data.total_entradas_estoque} produto(s) tiveram estoque atualizado!`);
      } else if (data.total_itens_recebidos > 0) {
        toast.info('Nenhum produto vinculado aos itens. Estoque não foi atualizado automaticamente.');
      }
      
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Erro ao registrar recebimento:', error);
      toast.error(error.response?.data?.detail || 'Erro ao registrar recebimento');
    } finally {
      setSubmitting(false);
    }
  };

  if (!pedido) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Confirmar Recebimento - {pedido.numero}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Info do pedido */}
          <div className="bg-muted/50 p-3 rounded-lg text-sm">
            <p><strong>Fornecedor:</strong> {pedido.fornecedor_nome}</p>
            {pedido.projeto_nome && <p><strong>Projeto:</strong> {pedido.projeto_nome}</p>}
          </div>

          {/* Tipo de entrega */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Tipo de entrega</Label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setTipoEntrega('estoque')}
                data-testid="tipo-entrega-estoque"
                className={`flex items-start gap-3 rounded-lg border p-3 text-left transition-colors ${
                  tipoEntrega === 'estoque'
                    ? 'border-primary bg-primary/5 ring-1 ring-primary'
                    : 'border-border hover:bg-muted/50'
                }`}
              >
                <Warehouse className={`h-5 w-5 mt-0.5 ${tipoEntrega === 'estoque' ? 'text-primary' : 'text-muted-foreground'}`} />
                <div>
                  <p className="text-sm font-medium">Dar entrada no estoque</p>
                  <p className="text-xs text-muted-foreground">Materiais entram no estoque selecionado</p>
                </div>
              </button>
              <button
                type="button"
                onClick={() => {
                  setTipoEntrega('entrega_direta');
                  // Colapsa para uma única linha de quantidade por item
                  setItensRecebimento(prev => prev.map(it => ({
                    ...it,
                    distribuicao: [it.distribuicao[0] || { local_id: '', local_nome: '', quantidade: it.quantidade_pendente }]
                  })));
                }}
                data-testid="tipo-entrega-direta"
                className={`flex items-start gap-3 rounded-lg border p-3 text-left transition-colors ${
                  tipoEntrega === 'entrega_direta'
                    ? 'border-primary bg-primary/5 ring-1 ring-primary'
                    : 'border-border hover:bg-muted/50'
                }`}
              >
                <Truck className={`h-5 w-5 mt-0.5 ${tipoEntrega === 'entrega_direta' ? 'text-primary' : 'text-muted-foreground'}`} />
                <div>
                  <p className="text-sm font-medium">Entrega direta ao cliente</p>
                  <p className="text-xs text-muted-foreground">Fornecedor entrega direto — não movimenta estoque</p>
                </div>
              </button>
            </div>
            {tipoEntrega === 'entrega_direta' && (
              <div
                className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800"
                data-testid="entrega-direta-info"
              >
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>
                  O estoque <strong>não será movimentado</strong>. A entrega será vinculada ao projeto
                  {pedido.projeto_nome ? <> <strong>{pedido.projeto_nome}</strong></> : ' do pedido'}.
                </span>
              </div>
            )}
          </div>

          {/* Lista de itens */}
          <div className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h4 className="font-medium flex items-center gap-2">
                <Warehouse className="h-4 w-4" />
                Itens para Recebimento
              </h4>
              {/* Aplicar o mesmo local a todos os itens */}
              {tipoEntrega === 'estoque' && itensRecebimento.some(i => i.quantidade_pendente > 0) && (
                <div className="flex items-center gap-2" data-testid="aplicar-local-todos">
                  <Label className="text-xs text-muted-foreground whitespace-nowrap">Mesmo estoque p/ todos:</Label>
                  <Select value={localGlobal} onValueChange={aplicarLocalTodos}>
                    <SelectTrigger className="w-[220px] h-8" data-testid="select-local-global">
                      <SelectValue placeholder="Aplicar local a todos" />
                    </SelectTrigger>
                    <SelectContent>
                      {locaisEstoque.map(local => (
                        <SelectItem key={local.id} value={local.id}>{local.nome}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            
            {itensRecebimento.map((item, itemIndex) => (
              <div 
                key={item.item_id || itemIndex}
                className="border rounded-lg p-4 space-y-3"
                data-testid={`item-recebimento-${itemIndex}`}
              >
                {/* Cabeçalho do item */}
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium">{item.descricao}</p>
                    <p className="text-sm text-muted-foreground">
                      Pedido: {item.quantidade_pedida} | 
                      Recebido: {item.quantidade_recebida_anterior} | 
                      <span className="text-primary font-medium"> Pendente: {item.quantidade_pendente}</span>
                    </p>
                  </div>
                  {!item.produto_id && (
                    <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
                      <AlertCircle className="h-3 w-3 inline mr-1" />
                      Sem produto vinculado
                    </span>
                  )}
                </div>

                {/* Distribuição por local (apenas entrada em estoque) */}
                {item.quantidade_pendente > 0 && tipoEntrega === 'estoque' && (
                  <div className="space-y-2 pl-4 border-l-2 border-primary/20">
                    <Label className="text-xs text-muted-foreground">
                      Distribuição por local de armazenamento:
                    </Label>
                    
                    {item.distribuicao.map((dist, localIndex) => (
                      <div key={localIndex} className="flex items-center gap-2">
                        <Select
                          value={dist.local_id}
                          onValueChange={(value) => updateLocalDistribuicao(itemIndex, localIndex, 'local_id', value)}
                        >
                          <SelectTrigger className="w-[250px]" data-testid={`select-local-${itemIndex}-${localIndex}`}>
                            <SelectValue placeholder="Selecione o local" />
                          </SelectTrigger>
                          <SelectContent>
                            {locaisEstoque.map(local => (
                              <SelectItem key={local.id} value={local.id}>
                                {local.nome}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        
                        <Input
                          type="number"
                          min="0"
                          max={item.quantidade_pendente}
                          value={dist.quantidade}
                          onChange={(e) => updateLocalDistribuicao(itemIndex, localIndex, 'quantidade', e.target.value)}
                          className="w-24"
                          data-testid={`input-quantidade-${itemIndex}-${localIndex}`}
                        />
                        <span className="text-sm text-muted-foreground">unid.</span>
                        
                        {item.distribuicao.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeLocalDistribuicao(itemIndex, localIndex)}
                          >
                            <Minus className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                    
                    {/* Botão para adicionar mais locais */}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => addLocalDistribuicao(itemIndex)}
                      className="mt-1"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Adicionar outro local
                    </Button>
                    
                    {/* Resumo da distribuição */}
                    <div className="text-sm mt-2">
                      <span className={getTotalDistribuido(item) === item.quantidade_pendente ? 'text-green-600' : 'text-orange-600'}>
                        {getTotalDistribuido(item) === item.quantidade_pendente ? (
                          <Check className="h-4 w-4 inline mr-1" />
                        ) : null}
                        Total a receber: {getTotalDistribuido(item)} de {item.quantidade_pendente}
                      </span>
                    </div>
                  </div>
                )}
                
                {/* Quantidade recebida (entrega direta ao cliente) */}
                {item.quantidade_pendente > 0 && tipoEntrega === 'entrega_direta' && (
                  <div className="space-y-2 pl-4 border-l-2 border-amber-300">
                    <Label className="text-xs text-muted-foreground">
                      Quantidade recebida pelo cliente:
                    </Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min="0"
                        max={item.quantidade_pendente}
                        value={item.distribuicao[0]?.quantidade ?? 0}
                        onChange={(e) => updateLocalDistribuicao(itemIndex, 0, 'quantidade', e.target.value)}
                        className="w-28"
                        data-testid={`input-quantidade-direta-${itemIndex}`}
                      />
                      <span className="text-sm text-muted-foreground">de {item.quantidade_pendente} unid.</span>
                    </div>
                  </div>
                )}

                {item.quantidade_pendente === 0 && (
                  <p className="text-sm text-green-600 flex items-center gap-1">
                    <Check className="h-4 w-4" />
                    Item totalmente recebido
                  </p>
                )}
              </div>
            ))}
          </div>

          {/* Comentário/observações do recebimento */}
          <div className="space-y-1.5">
            <Label className="text-sm font-medium flex items-center gap-1.5">
              <Package className="h-4 w-4 text-muted-foreground" />
              Observações do recebimento
            </Label>
            <Textarea
              value={observacao}
              onChange={(e) => setObservacao(e.target.value)}
              placeholder="Ex.: material chegou com avaria na embalagem, NF nº 1234, conferido por João..."
              rows={2}
              data-testid="input-observacao-recebimento"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={submitting || loading}
            data-testid="btn-confirmar-recebimento"
          >
            {submitting ? 'Registrando...' : 'Confirmar Recebimento'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DialogReceberPedido;
